import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';

// Finance V2, step 3: everything the guided assessment collected, with where
// each value came from, editable before the analysis runs. Only changed values
// are sent back (edits: {row id: value}); the server reads them like answers.

const NUMERIC_KINDS = ['number', 'money', 'percent'];

const same = (a, b) => JSON.stringify(Array.isArray(a) ? [...a].sort() : a)
  === JSON.stringify(Array.isArray(b) ? [...b].sort() : b);

const sourceKey = (row) => {
  if (row.origin === 'review') return 'review';
  if (row.source === 'context') return row.origin || 'message';
  return row.source || '';
};

function ReviewInput({ row, value, disabled, onChange }) {
  if (row.kind === 'select') {
    const known = row.options.some((option) => option.value === value);
    return (
      <select className="finance-review-input" value={value || ''} disabled={disabled} aria-label={row.label}
        onChange={(event) => onChange(event.target.value)}>
        <option value="">—</option>
        {!known && value && <option value={value}>{value}</option>}
        {row.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  if (row.kind === 'multi_select') {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (option) => onChange(selected.includes(option)
      ? selected.filter((item) => item !== option) : [...selected, option]);
    return (
      <fieldset className="finance-review-choices" aria-label={row.label} disabled={disabled}>
        {row.options.map((option) => (
          <label key={option.value}>
            <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }
  if (row.kind === 'text' || row.kind === 'list') {
    const text = value ?? '';
    const rows = Math.min(4, Math.max(1, Math.ceil(text.length / 30)));
    return (
      <textarea className={`finance-review-input${rows > 1 ? ' is-multiline' : ''}`} value={text} disabled={disabled}
        aria-label={row.label} rows={rows} onChange={(event) => onChange(event.target.value)} />
    );
  }
  return (
    <span className="finance-review-field">
      <input type="text" className="finance-review-input" value={value ?? ''} disabled={disabled} aria-label={row.label}
        inputMode={NUMERIC_KINDS.includes(row.kind) ? 'decimal' : undefined}
        onChange={(event) => onChange(event.target.value)} />
      {row.unit && <span className="finance-review-unit">{row.unit}</span>}
    </span>
  );
}

export default function FinanceReviewCard({ review, disabled, onSubmit, t }) {
  const [edits, setEdits] = useState({});
  useEffect(() => { setEdits({}); }, [review]);

  const groups = useMemo(() => {
    const order = [];
    const byGroup = {};
    (review?.rows || []).forEach((row) => {
      const key = row.group || '';
      if (!byGroup[key]) { byGroup[key] = []; order.push(key); }
      byGroup[key].push(row);
    });
    return order.map((key) => ({ key, rows: byGroup[key] }));
  }, [review]);

  if (!review) return null;

  // "alternatives": the current value stays as it is, the officer fills another one to test (#step 6).
  const alternatives = review.columns === 'alternatives';
  const current = (row) => (row.id in edits ? edits[row.id] : row.value);
  const changed = Object.fromEntries((review.rows || [])
    .filter((row) => row.id in edits && !same(edits[row.id], row.value) && String(edits[row.id]).trim() !== '')
    .map((row) => [row.id, edits[row.id]]));
  const count = Object.keys(changed).length;

  const submit = () => {
    const answerText = alternatives
      ? (count ? t('workspace:reviewAlternativesSent', { count }) : t('workspace:reviewRecalculate'))
      : count
        ? t('workspace:reviewConfirmedChanges', { count })
        : t('workspace:reviewConfirmed');
    onSubmit(answerText, {
      question_id: review.id,
      field_key: null,
      question: review.question,
      input_type: 'review_table',
      edits: alternatives ? changed : Object.fromEntries((review.rows || [])
        .filter((row) => row.id in edits && !same(edits[row.id], row.value))
        .map((row) => [row.id, edits[row.id]])),
    });
  };

  return (
    <div className="finance-review">
      <div className="credit-question-title">{review.question}</div>
      <p className="credit-question-helper">{t('workspace:reviewHint')}</p>
      {groups.map((group) => (
        <table className="finance-review-table" key={group.key || 'case'}>
          <caption>{group.key ? group.key.charAt(0).toUpperCase() + group.key.slice(1) : t('workspace:reviewCaseGroup')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('workspace:reviewColumnInfo')}</th>
              {alternatives && <th scope="col">{t('workspace:reviewColumnCurrent')}</th>}
              <th scope="col">{t(alternatives ? 'workspace:reviewColumnAlternative' : 'workspace:reviewColumnValue')}</th>
              <th scope="col">{t(alternatives ? 'workspace:reviewColumnNote' : 'workspace:reviewColumnSource')}</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => {
              const unknown = row.status === 'unknown' && !(row.id in edits);
              const source = sourceKey(row);
              return (
                <tr key={row.id} className={`${unknown ? 'is-unknown' : ''}${row.error ? ' has-error' : ''}`.trim()}>
                  <th scope="row">
                    {row.label}
                    {unknown && <span className="finance-review-badge">{t('workspace:reviewUnknown')}</span>}
                  </th>
                  {alternatives && <td className="finance-review-current">{row.current}{row.unit ? ` ${row.unit}` : ''}</td>}
                  <td>
                    <ReviewInput row={row} value={current(row)} disabled={disabled}
                      onChange={(value) => setEdits((prev) => ({ ...prev, [row.id]: value }))} />
                    {row.error && <span className="finance-review-error" role="alert">{t('workspace:reviewError')}</span>}
                    {row.benchmark && <small className="finance-review-benchmark">{row.benchmark}</small>}
                  </td>
                  <td className="finance-review-source" title={row.note || undefined}>
                    {alternatives && row.note && <span>{row.note}</span>}
                    {source && <span>{t(`workspace:reviewSource_${source}`)}</span>}
                    {(row.refs || []).map((ref) => (
                      <a key={ref.url} href={ref.url} target="_blank" rel="noopener noreferrer">{ref.title || ref.url}</a>
                    ))}
                    {!alternatives && row.note && ['web', 'assumption', 'calculation'].includes(row.source)
                      && <small>{row.note}</small>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}
      <div className="credit-question-actions">
        <button type="button" className="credit-submit-btn" disabled={disabled} onClick={submit}>
          <span>{t(alternatives ? 'workspace:reviewRecalculate' : 'workspace:reviewConfirm')}</span><FaArrowRight />
        </button>
      </div>
    </div>
  );
}
